import React, { SyntheticEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useHistory } from 'react-router-dom';
import { useAuth } from '../AuthenticationContext';
import { useConfig } from '../../ConfigContext';
import { CodePayload } from '../Authentication.model';

interface LocationRegister {
  email?: string;
  password?: string;
  provider?: string;
  idToken?: string;
  hash: string;
  expiryDate: string;
  isEmailVerified: boolean;
  from: {
    pathname: string;
  };
}

export function Otp(): JSX.Element {
  const { t } = useTranslation();
  const location = useLocation<LocationRegister>();
  const history = useHistory();
  const auth = useAuth();
  const inputEl = useRef(null);
  const config = useConfig();

  const [isEmailVerified, setIsEmailVerified] = useState(false);

  const sendCode = (payload: CodePayload, token: string | null = null):void => {
    if (location.state.provider) {
      auth.signinWithProvider(
        { idToken: location.state.idToken },
        location.state.provider,
        '',
        payload,
      );
    } else {
      auth.signin(
        {
          email: location.state.email,
          password: location.state.password,
        },
        token,
        payload,
      );
    }
  };

  const handleSubmit = (event: SyntheticEvent): void => {
    event.preventDefault();

    const payload: CodePayload = {
      hash: location.state.hash,
      expiryDate: location.state.expiryDate,
      code: (event.target as Element).querySelector('input').value,
    };

    if (config.clientConfig.recaptchaEnabled) {
      window.grecaptcha.execute(config.serverConfig.website.recaptcha.clientId, { action: 'sendCode' })
        .then((token) => {
          sendCode(payload, token);
        });
    } else {
      sendCode(payload);
    }
  };

  const resendCode = async (): Promise<void> => {
    if (config.clientConfig.recaptchaEnabled) {
      const token = await window.grecaptcha.execute(
        config.serverConfig.website.recaptcha.clientId,
        { action: 'login' },
      );
      await auth.signin({
        email: location.state.email,
        password: location.state.password,
      }, token);
    } else {
      await auth.signin({
        email: location.state.email,
        password: location.state.password,
      });
    }
  };

  useEffect(() => {
    // check if the email is validate
    if (location?.state?.isEmailVerified) {
      setIsEmailVerified(location?.state?.isEmailVerified);
    }

    // check if password and email parameters existed
    if ((!location?.state?.password || !location?.state?.email)
     && (!location?.state?.provider || !location?.state?.idToken)) {
      history.push('/');
    }

    // give the focus to the code input
    inputEl.current.focus();
  }, []);

  return (
    <div className='auth-card-container card-bhvra'>
      <div className='row text-center'>
        <div className='col-12'>
          <h2>{t('auth.register.sign-up')}</h2>
        </div>
      </div>
      <form name='mfa-form' className='form-light mt-4' onSubmit={handleSubmit}>
        <div className='form-group'>
          <label htmlFor='code'>
            {t(isEmailVerified ? 'auth.otp.text-mfa' : 'auth.register.send-confirmation')}
          </label>
          <input
            ref={inputEl}
            className='form-control mt-3'
            name='code'
            type='text'
            data-cy='validation-code-input'
            required
          />
        </div>
        <div
          onClick={resendCode}
          className='text-right font-weight-bold mt-1 pointer underline-dotted'
        >
          {t('auth.otp.resend-code')}
        </div>
        <div className='form-group mt-4'>
          <button
            type='submit'
            data-cy='submit-button'
            className='btn btn-primary-game btn-block'
          >
            {t('button.confirm')}
          </button>
        </div>
      </form>
    </div>
  );
}
